import React from 'react';

import { ChevronDownIcon, ChevronRightIcon, CloseIcon, PlusIcon } from '../../shared/Icons';
import { SettingsGroup, SettingsRow } from '../slotBookingEditor/settingsUi';
import { TextButton } from '../slotBookingEditor/SlotBookingEditor.styles';

interface TextSettingProps {
  label: string;
  value: string;
  placeholder?: string;
  description?: React.ReactNode;
  multiline?: boolean;
  onChange: (value: string) => void;
}

export const TextSetting: React.FC<TextSettingProps> = ({
  label,
  value,
  placeholder,
  description,
  multiline,
  onChange,
}) => (
  <SettingsRow label={label} description={description} stacked>
    {multiline ? (
      <textarea
        className="qe-textarea"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        type="text"
        className="sb-text-input"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </SettingsRow>
);

interface ListSectionProps<T extends { id: string }> {
  title: string;
  itemName: string;
  items: T[];
  footer?: React.ReactNode;
  minItems?: number;
  create: () => T;
  onChange: (items: T[]) => void;
  renderItem: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
}

/** An ordered list of editable items with add, remove and reorder controls. */
export const ListSection = <T extends { id: string }>({
  title,
  itemName,
  items,
  footer,
  minItems = 0,
  create,
  onChange,
  renderItem,
}: ListSectionProps<T>) => {
  const update = (index: number, patch: Partial<T>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <SettingsGroup title={title} footer={footer}>
      {items.map((item, index) => (
        <SettingsRow key={item.id} label="" stacked>
          <div className="qe-item">
            <div className="qe-item-head">
              <span>
                {itemName} {index + 1}
              </span>
              <span className="qe-item-actions">
                <TextButton
                  type="button"
                  $tone="muted"
                  aria-label={`Move ${itemName} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ChevronRightIcon size={13} className="qe-rotate-up" />
                </TextButton>
                <TextButton
                  type="button"
                  $tone="muted"
                  aria-label={`Move ${itemName} down`}
                  disabled={index === items.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ChevronDownIcon size={13} />
                </TextButton>
                <TextButton
                  type="button"
                  $tone="danger"
                  aria-label={`Remove ${itemName}`}
                  disabled={items.length <= minItems}
                  onClick={() => onChange(items.filter((_, i) => i !== index))}
                >
                  <CloseIcon size={13} />
                </TextButton>
              </span>
            </div>
            {renderItem(item, (patch) => update(index, patch))}
          </div>
        </SettingsRow>
      ))}
      <SettingsRow label={items.length ? `Add another ${itemName.toLowerCase()}` : `Add a ${itemName.toLowerCase()}`}>
        <TextButton type="button" onClick={() => onChange([...items, create()])}>
          <PlusIcon size={13} />
          Add
        </TextButton>
      </SettingsRow>
    </SettingsGroup>
  );
};
